import { authFetch } from '../auth/authFetch';
import { readFileResponse } from './fileResponse';
import {
  CHUNK_UPLOAD_THRESHOLD_BYTES,
  shouldChunkUpload,
  uploadInChunks,
} from './chunkedUpload';
import {
  peekWorkspaceIdentity,
  redirectToSignIn,
  requireIdentityHeaders,
} from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );

export const CUSTODIAN_AUTHORITY_DECISIONS = ['APPROVED', 'REJECTED'];

function buildQueryString(query) {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.CUSTODIAN);
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function fallbackMessageForStatus(status) {
  if (status === 403) return 'Custodian access required.';
  if (status === 404) {
    return 'Package not found or not available for Custodian review.';
  }
  if (status === 409) {
    return 'Package is not currently eligible for this action.';
  }
  return `Request failed (${status})`;
}

function createRequestError(message, status) {
  const error = new Error(message);
  error.status = status;
  if (status === 403) {
    error.authorizationError = true;
  }
  return error;
}

async function messageFromErrorResponse(response) {
  let message = fallbackMessageForStatus(response.status);
  try {
    const errorPayload = await response.json();
    const backendMessage = extractErrorMessage(errorPayload, response.status);
    if (backendMessage && backendMessage !== `Request failed (${response.status})`) {
      message = backendMessage;
    } else if (response.status === 400) {
      message = backendMessage;
    }
  } catch {
    // Keep status fallback when body is empty or not JSON.
  }
  if (response.status === 400 && message === `Request failed (${response.status})`) {
    message = fallbackMessageForStatus(response.status);
  }
  return message;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query, headers } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function getCustodianApiConfig() {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.CUSTODIAN);
  return {
    apiBaseUrl: API_BASE_URL,
    userId: identity.userId,
    orgId: identity.orgId,
  };
}

export function getCustodianAuthorityReview({ page = 1, pageSize = 20 } = {}) {
  return request('/v1/custodian/authority-review', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function getCustodianPendingAdmission({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/pending-admission', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function listCustodianVaultAssets({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/vault-assets', {
    query: {
      page,
      page_size: pageSize,
    },
  });
}

export function getCustodianPackage(packageId) {
  return request(`/v1/custodian/packages/${packageId}`);
}

export function bulkCustodianPackageAction(packageIds, action, { mfaCode, mfaToken } = {}) {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.CUSTODIAN);
  const headers = {};
  const code = (mfaCode || '').trim();
  if (code) headers['X-Mfa-Code'] = code;
  // Fresh Microsoft step-up token when the user confirmed with MFA; otherwise
  // the session token (the backend ignores it unless it is an Entra MFA token).
  const token = (mfaToken || '').trim() || identity.accessToken;
  if (token) headers['X-Mfa-Token'] = token;
  return request('/v1/custodian/packages/bulk-actions', {
    method: 'POST',
    headers,
    body: {
      package_ids: packageIds,
      action,
    },
  });
}

function mfaHeaders(mfa) {
  const headers = {};
  if (mfa?.token) headers['X-Mfa-Token'] = mfa.token;
  if (mfa?.code) headers['X-Mfa-Code'] = mfa.code;
  return headers;
}

async function readZipDownload(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }
  const blob = await response.blob();
  const match = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '');
  return { blob, fileName: match?.[1] || 'watermarked-package-copies.zip' };
}

export function exportCustodianWatermarkedPackages(packageIds, mfa) {
  return authFetch(`${API_BASE_URL}/v1/custodian/packages/watermarked-export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
      ...mfaHeaders(mfa),
    },
    body: JSON.stringify({ package_ids: packageIds }),
  }).then(readZipDownload);
}

export function importCustodianWatermarkedPackageList(file, mfa) {
  const body = new FormData();
  body.append('file', file);
  return authFetch(`${API_BASE_URL}/v1/custodian/packages/watermarked-export/import`, {
    method: 'POST',
    headers: {
      ...getContextHeaders(),
      ...mfaHeaders(mfa),
    },
    body,
  }).then(readZipDownload);
}

export function submitCustodianAuthorityDecision(packageId, decision) {
  return request(`/v1/custodian/packages/${packageId}/authority-decision`, {
    method: 'POST',
    body: {
      decision,
    },
  });
}

export function admitCustodianPackage(packageId) {
  return request(`/v1/custodian/packages/${packageId}/admit`, {
    method: 'POST',
  });
}

export function getCustodianAdmissionStatus(packageId) {
  return request(`/v1/custodian/packages/${packageId}/admission-status`);
}

/** Independently verifiable admission evidence: manifest, receipts, mandate (EV-14/15). */
export function getCustodianEvidenceExport(packageId) {
  return request(`/v1/custodian/packages/${packageId}/evidence-export`);
}

/**
 * Same evidence as a readable PDF statement or Excel workbook. The backend
 * puts the verification outcome in headers so it can be shown without
 * parsing the document.
 */
export async function downloadCustodianEvidenceExport(packageId, format = 'pdf') {
  const response = await authFetch(
    `${API_BASE_URL}/v1/custodian/packages/${packageId}/evidence-export${buildQueryString({ format })}`,
    { method: 'GET', headers: { ...getContextHeaders() } }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const verifiedHeader = response.headers.get('x-evidence-manifest-verified');
  const receiptsVerified = Number(response.headers.get('x-evidence-receipts-verified'));
  const receiptsTotal = Number(response.headers.get('x-evidence-receipts-total'));
  return {
    blob,
    fileName: match?.[1] || `evidence-${packageId}.${format}`,
    manifestHashVerified: verifiedHeader == null ? null : verifiedHeader === 'true',
    receiptsVerified: Number.isFinite(receiptsVerified) ? receiptsVerified : null,
    receiptsTotal: Number.isFinite(receiptsTotal) ? receiptsTotal : null,
  };
}

export function listCustodianCopyRequests({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/copy-requests', {
    query: { page, page_size: pageSize },
  });
}

export function issueCustodianCertifiedCopy(requestId) {
  return request(`/v1/custodian/copy-requests/${requestId}/issue`, {
    method: 'POST',
  });
}

export function listCustodianIssuedCopies({ page = 1, pageSize = 50 } = {}) {
  return request('/v1/custodian/copies', {
    query: { page, page_size: pageSize },
  });
}

export function getCustodianPackageCopyContexts(packageId) {
  return request(`/v1/custodian/packages/${packageId}/copy-contexts`);
}

export function getCustodianFileCopyContext(packageId, fileId) {
  return request(`/v1/custodian/packages/${packageId}/files/${fileId}/copy-context`);
}

async function requestBlob(path, { query } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method: 'GET',
    headers: {
      ...getContextHeaders(),
    },
  });
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    throw createRequestError(await messageFromErrorResponse(response), response.status);
  }
  return readFileResponse(response);
}

export function downloadCustodianIssuedCopy(copyId) {
  return requestBlob(`/v1/custodian/copies/${copyId}/content`);
}

export function downloadCustodianFileRendition(packageId, fileId, { disposition = 'inline' } = {}) {
  return requestBlob(`/v1/custodian/packages/${packageId}/files/${fileId}/rendition`, {
    query: { disposition },
  });
}

export function downloadCustodianOriginal(packageId, fileId) {
  return requestBlob(`/v1/custodian/packages/${packageId}/files/${fileId}/original`);
}

// --------------------------------------------------------------------------
// Upload batch on behalf of the program's originator.
// The custodian role is the existing workspace role; no extra role is needed.
// After the files (and optional tape) are confirmed, `completeCustodianUploadBatch`
// classifies, runs admission checks and certifies the batch on the originator's
// behalf when they pass clean; otherwise it stays with the originator to cure.
// --------------------------------------------------------------------------

const CUSTODIAN_UPLOAD_BASE = '/v1/custodian/uploads';

/** Active programs this custodian organisation is assigned to. */
export function listCustodianUploadPrograms() {
  return request(`${CUSTODIAN_UPLOAD_BASE}/programs`);
}

export function listCustodianProgramIngestChannels(programId) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/programs/${programId}/ingest-channels`);
}

export function listCustodianProgramInboxFiles(programId, channel) {
  return request(
    `${CUSTODIAN_UPLOAD_BASE}/programs/${programId}/ingest-channels/${encodeURIComponent(channel)}/files`
  );
}

export function importCustodianUploadInboxFiles(packageId, { channel, fileNames } = {}) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/files/import`, {
    method: 'POST',
    body: { channel, file_names: fileNames },
  });
}

/** Open a DRAFT batch owned by the program's originator organisation. */
export function createCustodianUploadPackage({ programId, referenceNumber }) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/packages`, {
    method: 'POST',
    body: {
      program_id: programId,
      reference_number: referenceNumber || null,
    },
  });
}

export function createCustodianUploadSession(packageId, { fileName, mimeType, sizeBytes } = {}) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/files/upload-session`, {
    method: 'POST',
    body: {
      file_name: fileName,
      mime_type: mimeType || null,
      size_bytes: sizeBytes ?? null,
    },
  });
}

/**
 * Use the backend-returned upload_url only when it is the custodian Ingest
 * proxy path. Azure blob URLs and SAS query strings are ignored.
 */
export function resolveCustodianUploadPath(uploadUrl, packageId, fileId) {
  const fallback = `${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/files/${fileId}/content`;
  if (!uploadUrl || typeof uploadUrl !== 'string') return fallback;
  const trimmed = uploadUrl.trim();
  if (!trimmed) return fallback;
  if (/[?&]sig=/i.test(trimmed) || /blob\.core\.windows\.net/i.test(trimmed)) return fallback;
  if (trimmed.startsWith(`${CUSTODIAN_UPLOAD_BASE}/packages/`) && trimmed.endsWith('/content')) {
    return trimmed.split('?')[0];
  }
  try {
    const parsed = new URL(trimmed, API_BASE_URL);
    if (
      parsed.pathname.startsWith(`${CUSTODIAN_UPLOAD_BASE}/packages/`) &&
      parsed.pathname.endsWith('/content')
    ) {
      return parsed.pathname;
    }
  } catch {
    // Fall through to the known proxy path.
  }
  return fallback;
}

/**
 * PUT raw file bytes through the custodian Ingest proxy.
 * Must not send application/json and must not target Azure/SAS URLs.
 */
export function uploadCustodianFileContentChunked(
  packageId,
  fileId,
  file,
  { contentType, chunkSize, onProgress, maxAttempts } = {}
) {
  const base = `${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/files/${fileId}/content/chunks`;
  const extra = contentType ? { 'X-Upload-Content-Type': contentType } : {};
  return uploadInChunks(file, {
    chunkSize,
    maxAttempts,
    onProgress,
    putChunk: async (index, slice) => {
      const response = await authFetch(`${API_BASE_URL}${base}/${index}`, {
        method: 'PUT',
        headers: { ...getContextHeaders(), ...extra, 'Content-Type': 'application/octet-stream' },
        body: slice,
      });
      return parseResponse(response);
    },
    complete: (chunkCount) =>
      request(`${base}/complete`, {
        method: 'POST',
        body: { chunk_count: chunkCount },
        headers: extra,
      }),
  });
}

export async function uploadCustodianFileContent(
  packageId,
  fileId,
  data,
  { uploadUrl, contentType, onProgress, chunkThreshold = CHUNK_UPLOAD_THRESHOLD_BYTES } = {}
) {
  if (shouldChunkUpload(data, chunkThreshold)) {
    // 2,000-PDF ZIPs (~200 MB) as one PUT were cut by the ingress mid-stream.
    return uploadCustodianFileContentChunked(packageId, fileId, data, { contentType, onProgress });
  }
  const path = resolveCustodianUploadPath(uploadUrl, packageId, fileId);
  const headers = { ...getContextHeaders() };
  if (contentType) headers['Content-Type'] = contentType;
  const response = await authFetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers,
    body: data,
  });
  return parseResponse(response);
}

export function confirmCustodianUpload(packageId, fileId, payload = {}) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/files/${fileId}/confirm`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Attach (or replace) the batch's optional loan tape (CSV / XLSX) as multipart
 * form data. The browser sets the multipart boundary, so no Content-Type is forced.
 */
export async function uploadCustodianPackageLoanTape(packageId, file) {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await authFetch(`${API_BASE_URL}${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/loan-tape`, {
    method: 'POST',
    headers: { ...getContextHeaders() },
    body: form,
  });
  return parseResponse(response);
}

/**
 * Finish a custodian batch: classify, run admission checks and, when no
 * blocking exception remains, certify on the originator's behalf. The
 * response reports `certified`, `readiness_status` and exception counts.
 */
export function completeCustodianUploadBatch(packageId) {
  return request(`${CUSTODIAN_UPLOAD_BASE}/packages/${packageId}/complete`, {
    method: 'POST',
    body: {},
  });
}
